/* eslint-disable max-len */
import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Row,
    Col,
    Input,
    Form,
    Button,
    Divider,
    Radio,
} from 'antd';
import MUIFormItem from '@/components/MUIFormItem';
import IncrementNumberInput from '@/components/IncrementNumberInput';
import InputSelect from '@/components/InputSelect';
import { selectors } from '@/redux/accommodation/hotel/create/model';
import { PlusIcon, DeleteIcon } from '@/assets/images/icons/screens';
import PriceItem from './PriceItem';

const PersonAndBed = ({ currency, validations }) => {
    const { t } = useTranslation();
    const room = useSelector(selectors.roomDetails);

    const [adultNumber, setAdultNumber] = useState(0);
    const [childNumber, setChildNumber] = useState(0);

    const options = useMemo(() => ([
        { label: t('%'), value: 4 },
        { label: t(currency), value: 5 },
    ]), [t]);

    return (
        <Row gutter={[28, 0]}>
            {/* Left Side: Adults */}
            <Col xs={24}>
                <Row align="middle" gutter={8}>
                    <Col xxl={8} xl={12} xs={24}>
                        <Form.Item name="adults">
                            <IncrementNumberInput
                                onChange={setAdultNumber}
                                label={` ${t('Adult')}`}
                                max={10}
                                defaultCount={room?.adults || adultNumber}
                            />
                        </Form.Item>
                    </Col>
                    <Col xxl={10} xl={12} xs={24}>
                        <div className="input-select-wrapper">
                            <div className="input-select-children">
                                <MUIFormItem
                                    label={t('Bed Type for adult')}
                                    name="bed_name_adult"
                                >
                                    <Input disabled={adultNumber === 0} />
                                </MUIFormItem>
                            </div>
                            <div>
                                <InputSelect
                                    posibleValue={adultNumber}
                                    isDisabled={adultNumber === 0}
                                    name="adult_bed_no"
                                />
                            </div>
                        </div>
                    </Col>
                    <Col span={24}>
                        <Form.List name="extra_adult_prices">
                            {(fields, { add, remove }) => (
                                <>
                                    <Row justify="end" gutter={16}>
                                        <Col xxl={6} xl={12} lg={24} sm={12} xs={24}>
                                            <Button
                                                className="button add-button-outlined new-section-btn"
                                                onClick={() => add()}
                                                block
                                            >
                                                <PlusIcon />
                                                {t('add extra adult policy').toUpperCase()}
                                            </Button>
                                        </Col>
                                    </Row>
                                    {fields.map((field, index) => (
                                        <div key={field.key} className="extra-adult-policy-item">
                                            <Row gutter={30}>
                                                <Col xxl={14} sm={4} xs={24}>
                                                    <div>
                                                        {`${t('extra adult')} ${index + 1}`}
                                                    </div>
                                                </Col>
                                                <Col xxl={8} sm={16} xs={24}>
                                                    <MUIFormItem
                                                        label={t('Price')}
                                                        name={[field.name, 'price']}
                                                        formListName="extra_adult_prices"
                                                    >
                                                        <Input
                                                            suffix={(
                                                                <>
                                                                    0.00
                                                                    <Form.Item
                                                                        name={[field.name, 'price_type']}
                                                                        rules={validations.extraAdultPriceType}
                                                                        noStyle
                                                                    >
                                                                        <Radio.Group
                                                                            options={options}
                                                                            optionType="button"
                                                                            buttonStyle="solid"
                                                                        />
                                                                    </Form.Item>
                                                                </>
                                                            )}
                                                        />
                                                    </MUIFormItem>
                                                </Col>
                                                <Col xxl={2} sm={4} xs={24}>
                                                    <Form.Item>
                                                        <Button
                                                            className="button btn-grey"
                                                            onClick={() => remove(field.name)}
                                                        >
                                                            <DeleteIcon />
                                                        </Button>
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Divider dashed />
                                        </div>
                                    ))}
                                </>
                            )}
                        </Form.List>
                    </Col>
                </Row>
            </Col>

            {/* Right Side: Children */}
            <Col xs={24}>
                <Row align="middle" gutter={8}>
                    <Col xxl={8} xl={12} xs={24}>
                        <Form.Item name="children">
                            <IncrementNumberInput
                                onChange={setChildNumber}
                                label={` ${t('Child')}`}
                                max={10}
                                defaultCount={room?.children || childNumber}
                            />
                        </Form.Item>
                    </Col>
                    <Col xxl={10} xl={12} xs={24}>
                        <div className="input-select-wrapper">
                            <div className="input-select-children">
                                <MUIFormItem
                                    label={t('Bed Type for child')}
                                    name="bed_name_children"
                                >
                                    <Input disabled={childNumber === 0} />
                                </MUIFormItem>
                            </div>
                            <div>
                                <InputSelect
                                    posibleValue={childNumber}
                                    isDisabled={childNumber === 0}
                                    name="children_bed_no"
                                />
                            </div>
                        </div>
                    </Col>
                    <Col span={24}>
                        <Form.List name="prices">
                            {(fields, { add, remove }) => (
                                <>
                                    <Row justify="end" gutter={16}>
                                        <Col xxl={6} xl={12} lg={24} sm={12} xs={24}>
                                            <Button
                                                className="button add-button-outlined new-section-btn"
                                                onClick={() => add()}
                                                block
                                            >
                                                <PlusIcon />
                                                {t('add child policy').toUpperCase()}
                                            </Button>
                                        </Col>
                                    </Row>
                                    {fields.map((field) => (
                                        <div key={field.key} className="child-price-policy-item">
                                            <Form.List
                                                name={[field.name, 'child_policy']}
                                                initialValue={[{}]}
                                            >
                                                {(childFields, { add: addChild, remove: removeChild }) => (
                                                    <>
                                                        {childFields.map((childField) => (
                                                            <PriceItem
                                                                key={childField.key}
                                                                field={childField}
                                                                remove={removeChild}
                                                                formListName="prices"
                                                                nestedFormListName="child_policy"
                                                                currency={currency}
                                                                sectionIndex={field.name}
                                                            />
                                                        ))}
                                                        <Row justify="end" gutter={16}>
                                                            <Col>
                                                                <Button
                                                                    className="button add-button-outlined"
                                                                    onClick={() => addChild()}
                                                                >
                                                                    <PlusIcon />
                                                                    {t('add policy').toUpperCase()}
                                                                </Button>
                                                            </Col>
                                                            <Col>
                                                                <Button
                                                                    className="button btn-grey"
                                                                    onClick={() => remove(field.name)}
                                                                >
                                                                    <DeleteIcon />
                                                                    {t('remove child policy').toUpperCase()}
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                        <Divider dashed />
                                                    </>
                                                )}
                                            </Form.List>
                                        </div>
                                    ))}
                                </>
                            )}
                        </Form.List>
                    </Col>
                </Row>
            </Col>
        </Row>
    );
};

export default PersonAndBed;
